import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Cloud, CloudOff, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export function GoogleCalendarSync() {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);

  useEffect(() => {
    // Check connection status async without blocking UI
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async (withLoading: boolean = true) => {
    if (withLoading) {
      setLoading(true);
    }
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-oauth', {
        body: { action: 'status' }
      });

      if (error) throw error;
      setConnected(data.connected);
      setLastSync(data.last_sync);
      return data;
    } catch (error: any) {
      console.error('Error checking connection:', error);
      return null;
    } finally {
      if (withLoading) {
        setLoading(false);
      }
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-oauth', {
        body: { action: 'authorize' }
      });

      if (error) throw error;

      // Open OAuth URL in popup
      window.open(data.auth_url, 'Google Calendar Authorization', 'width=600,height=600');
      
      // Poll for connection status
      const checkInterval = setInterval(async () => {
        const statusData = await checkConnectionStatus(false);
        if (statusData?.connected) {
          clearInterval(checkInterval);
          setConnected(true);
          toast.success('Google Calendar connected successfully');
        }
      }, 2000);

      // Stop polling after 2 minutes
      setTimeout(() => clearInterval(checkInterval), 120000);

    } catch (error: any) {
      console.error('Error connecting:', error);
      toast.error('Failed to connect to Google Calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (action: 'import' | 'export') => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: { action }
      });

      if (error) throw error;

      toast.success(
        action === 'import' 
          ? `Imported ${data.imported} events from Google Calendar`
          : 'Event exported to Google Calendar'
      );
      
      await checkConnectionStatus();
      setSyncDialogOpen(false);
    } catch (error: any) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync with Google Calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('google-calendar-oauth', {
        body: { action: 'disconnect' }
      });

      if (error) throw error;

      setConnected(false);
      toast.success('Disconnected from Google Calendar');
      setSyncDialogOpen(false);
    } catch (error: any) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Button variant="outline" disabled>
        <Cloud className="h-4 w-4 mr-2 animate-pulse" />
        <span className="text-xs">Checking...</span>
      </Button>
    );
  }

  if (!connected) {
    return (
      <Button variant="outline" onClick={handleConnect} disabled={loading}>
        <Cloud className="h-4 w-4 mr-2" />
        Connect Google Calendar
      </Button>
    );
  }

  return (
    <>
      <Button variant="outline" onClick={() => setSyncDialogOpen(true)}>
        <Cloud className="h-4 w-4 mr-2 text-green-600" />
        Google Calendar
        <Badge variant="outline" className="ml-2 text-xs">Connected</Badge>
      </Button>

      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Google Calendar Sync</DialogTitle>
            <DialogDescription>
              Your calendar is connected to Google Calendar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {lastSync && (
              <p className="text-sm text-muted-foreground">
                Last synced: {new Date(lastSync).toLocaleString()}
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Button 
                onClick={() => handleSync('import')}
                disabled={loading}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Import Events
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleDisconnect}
                disabled={loading}
                className="w-full"
              >
                <CloudOff className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Import events from your Google Calendar or disconnect the integration.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}